import { SkeletonList } from "@/design-system/components/ui";
import { useTeamBoard } from "@/hooks/buyers/useBuyerReports";
import { QueryErrorState } from "../../queryStates";
import { TeamBoard } from "../TeamBoard";

/**
 * The Specialists tab — the team board for the page's team. With no team
 * chosen (an all-teams caller on "All teams", or a caller on no team) there is
 * no board to draw, and the tab says which of the two it is.
 */
export function SpecialistsReportTab({
  teamId,
  reason,
}: {
  teamId: string | undefined;
  /** Why there is no team, for the empty state. */
  reason: "pick-a-team" | "no-team";
}) {
  const board = useTeamBoard(teamId);

  if (!teamId) {
    return (
      <p className="text-small text-gray-mid italic m-0" data-testid="board-no-team">
        {reason === "no-team"
          ? "You are not on a buyer team, so there is no board to show."
          : "Choose a team above to see its consultants side by side."}
      </p>
    );
  }

  if (board.isError) {
    return (
      <QueryErrorState
        what="the team board"
        onRetry={() => void board.refetch()}
        retrying={board.isFetching}
      />
    );
  }

  if (!board.data) return <SkeletonList rows={3} data-testid="board-loading" />;

  return <TeamBoard board={board.data} />;
}
